import { HttpClient } from '@ydistri/api-sdk';
import { getItem, LSKey, setItem } from '../store/localStore';

/**
 * The project header sent to the API. The header is named 'Project-Slug' per the API contract
 * (a neutral name for the project identifier); its value is currently the project's short name.
 */
export const PROJECT_SLUG_HEADER = 'Project-Slug';

/**
 * The project this tab is working with. Module scope on purpose: the module is evaluated once per
 * browsing context, so the value cannot leak between browser tabs - unlike localStorage, which every
 * tab of the origin shares.
 */
let selectedProjectShortName: string | undefined;

/**
 * Returns the short name of the project selected **in this tab** - the app's internal project
 * identity, used for RTK-Query cache keys and as query arguments - or undefined before a project is
 * selected. It always matches the `Project-Slug` default header on the shared API client, because
 * {@link setSelectedProjectApiContext} is the only writer of those two. (The last-used localStorage
 * key has a second writer, the `setSelectedProjectShortName` reducer, which is harmless - that key is
 * only a startup default.)
 *
 * Do not reach for localStorage instead: two tabs may legitimately show two different projects, and
 * a shared value makes each tab load the other's data (issue #673).
 */
export const getSelectedProjectShortName = (): string | undefined => selectedProjectShortName;

/**
 * Returns the project last opened in **any** tab. This is a startup default for a tab whose URL
 * carries no project - never the identity of the current tab; use {@link getSelectedProjectShortName}
 * for that.
 */
export const getLastUsedProjectShortName = (): string | undefined =>
  getItem(LSKey.SELECTED_PROJECT_SHORT_NAME);

/**
 * Selects the project for this tab: remembers it as the tab's project identity, sends its short name
 * in the project header on the shared API client, and stores it as the last-used project for tabs
 * opened later without a project in the URL.
 */
export const setSelectedProjectApiContext = (api: HttpClient, projectShortName: string): void => {
  selectedProjectShortName = projectShortName;
  api.instance.defaults.headers.common[PROJECT_SLUG_HEADER] = projectShortName;
  setItem(LSKey.SELECTED_PROJECT_SHORT_NAME, projectShortName);
};
